import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { TemplateVersionSelect } from '../../../common';

/**
 * TreatmentCard Component
 *
 * Individual treatment form card.
 *
 * @param {Object} props
 * @param {Object} props.treatment - Treatment data object
 * @param {number} props.index - Treatment index
 * @param {Object} props.errors - Form errors object
 * @param {Array} props.templateVersions - Available template versions
 * @param {Function} props.onTreatmentChange - Handler for treatment field changes
 * @param {Function} props.onRemove - Handler for removing treatment
 * @param {boolean} props.canRemove - Whether treatment can be removed
 * @param {Function} props.getSafeTemplateVersion - Function to get safe template version value
 * @param {Array} props.securityPolicies - Security policies for tooltip display
 */
function TreatmentCard({
  treatment,
  index,
  errors,
  templateVersions,
  onTreatmentChange,
  onRemove,
  canRemove,
  getSafeTemplateVersion,
  securityPolicies = [],
}) {
  const theme = useTheme();

  return (
    <Box
      key={treatment.treatment_id || `treatment-${index}`}
      sx={{
        mb: 1.5,
        '&:last-child': { mb: 0 },
      }}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            pb={1}
            borderBottom={`1px solid ${theme.palette.divider}`}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: theme.palette.text.primary,
                }}
              >
                Treatment {index + 1}
              </Typography>
              {treatment.treatment_type === 'control' && (
                <Chip
                  label="Control"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
              )}
            </Box>
            {canRemove && (
              <IconButton
                size="small"
                onClick={() => onRemove(index)}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.error.light ||
                      theme.palette.background.default,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {/* Treatment Name, Type, and Template Version in one row */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ pt: 1.5 }}>
                <TextField
                  fullWidth
                  label="Treatment Name"
                  value={treatment.name}
                  onChange={e =>
                    onTreatmentChange(index, 'name', e.target.value)
                  }
                  error={!!errors[`treatment_${index}_name`]}
                  helperText={errors[`treatment_${index}_name`]}
                  required
                  size="small"
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ pt: 1.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={treatment.treatment_type}
                    onChange={e =>
                      onTreatmentChange(index, 'treatment_type', e.target.value)
                    }
                  >
                    <MenuItem value="control">Control</MenuItem>
                    <MenuItem value="treatment">Treatment</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ pt: 1.5 }}>
                <TemplateVersionSelect
                  versions={templateVersions}
                  value={getSafeTemplateVersion(treatment.template_version)}
                  onChange={(versionValue, versionObj) => {
                    onTreatmentChange(
                      index,
                      'template_version',
                      versionValue ? parseInt(versionValue) : ''
                    );
                  }}
                  label="Template Version"
                  size="small"
                  fullWidth={true}
                  displayEmpty={true}
                  showTooltip={true}
                  securityPolicies={securityPolicies}
                  // Map version data structure for experiments
                  // Experiments API returns: { version: number, created_at: string }
                  // Advanced API returns: { version_number: number, status: string, created_at: string }
                  getVersionValue={version => {
                    if (version.version_number !== undefined) {
                      return version.version_number;
                    }
                    // For experiments, version.version is a number
                    return version.version !== undefined
                      ? parseInt(version.version)
                      : '';
                  }}
                  getVersionNumber={version =>
                    version.version_number || version.version || ''
                  }
                  getVersionStatus={version => version.status || null}
                  getVersionDate={version => version.created_at || null}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ pt: 1.5 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={treatment.description}
                  onChange={e =>
                    onTreatmentChange(index, 'description', e.target.value)
                  }
                  multiline
                  rows={4}
                  size="small"
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ pt: 1.5 }}>
                <TextField
                  fullWidth
                  label="Hypothesis"
                  value={treatment.hypothesis}
                  onChange={e =>
                    onTreatmentChange(index, 'hypothesis', e.target.value)
                  }
                  placeholder="What do you expect this treatment to achieve?"
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

export default TreatmentCard;
